import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, MapPin } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config/env';

type BookingLinkData = {
  slug: string;
  label: string;
  mode: 'CONFIRM' | 'REQUEST';
  organization?: { name: string; timezone: string };
  services: Array<{
    id: string;
    displayName: string;
    category?: string | null;
    description?: string | null;
    durationMinutes: number;
    price: number;
    currency: string;
    classMeta?: {
      isClass: boolean;
      classCapacity: number;
      classOnlineBookingEnabled: boolean;
      classPortalVisible: boolean;
      classNotificationsEnabled: boolean;
      classParticipantInstructions: string;
    };
  }>;
  locations: Array<{
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
    isPrimary?: boolean;
  }>;
};

type Slot = {
  slotKey: string;
  providerId: string;
  providerName: string;
  appointmentTypeId: string;
  locationId?: string | null;
  startTime: string;
  endTime: string;
  durationMinutes: number;
};

function tomorrowDateId() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function addDays(dateId: string, days: number) {
  const date = new Date(`${dateId}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatSlot(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatPrice(value: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value || 0);
}

export default function PublicBookingPage() {
  const { slug = '' } = useParams();
  const [link, setLink] = useState<BookingLinkData | null>(null);
  const [serviceId, setServiceId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [dateId, setDateId] = useState(tomorrowDateId());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', dob: '', email: '', phone: '', notes: '', communicationTermsAccepted: false, website: '' });
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API_BASE}/portal/public-booking/${slug}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(await response.text());
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const data = payload.data as BookingLinkData;
        setLink(data);
        setServiceId(data.services[0]?.id || '');
        setLocationId(data.locations.find((location) => location.isPrimary)?.id || data.locations[0]?.id || '');
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Booking link could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!serviceId) return;
    const params = new URLSearchParams({
      appointmentTypeId: serviceId,
      startDate: dateId,
      endDate: addDays(dateId, 14),
    });
    if (locationId) params.set('locationId', locationId);
    setSlotLoading(true);
    setSelectedSlot(null);
    fetch(`${API_BASE}/portal/public-booking/${slug}/slots?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(await response.text());
        return response.json();
      })
      .then((payload) => setSlots(payload.data || []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Unable to load available times.'))
      .finally(() => setSlotLoading(false));
  }, [dateId, locationId, serviceId, slug]);

  const selectedService = useMemo(() => link?.services.find((service) => service.id === serviceId) || null, [link, serviceId]);

  async function submit() {
    if (!selectedSlot) {
      setError('Select an available time.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/portal/public-booking/${slug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          appointmentTypeId: serviceId,
          providerId: selectedSlot.providerId,
          locationId: locationId || selectedSlot.locationId || undefined,
          slotStartTime: selectedSlot.startTime,
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      setSuccess(link?.mode === 'CONFIRM' ? 'Your booking is confirmed.' : 'Your booking request has been sent.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit booking.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-slate-50 p-6 text-slate-600">Loading booking link...</div>;
  if (!link) return <div className="min-h-screen bg-slate-50 p-6 text-rose-700">{error || 'Booking link not found.'}</div>;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <main className="mx-auto max-w-5xl space-y-5">
        <header className="rounded-lg border border-slate-200 bg-white p-5">
          <p className="text-sm font-medium text-slate-500">{link.organization?.name || 'Clinic booking'}</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">{link.label}</h1>
        </header>

        {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
        {success ? <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div> : null}

        <section className="grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">Service</span>
                <select className="w-full rounded-md border border-slate-300 px-3 py-2" value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
                  {link.services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">Location</span>
                <select className="w-full rounded-md border border-slate-300 px-3 py-2" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                  {link.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">From date</span>
                <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="date" value={dateId} onChange={(event) => setDateId(event.target.value)} />
              </label>
            </div>

            {selectedService ? (
              <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-medium text-slate-900">{selectedService.displayName}</p>
                <p>{selectedService.durationMinutes} minutes · {formatPrice(selectedService.price, selectedService.currency)}</p>
                {selectedService.classMeta?.isClass ? (
                  <p className="mt-1 font-medium text-emerald-700">
                    Class · {selectedService.classMeta.classCapacity} spaces
                    {selectedService.classMeta.classNotificationsEnabled ? ' · reminders enabled' : ''}
                  </p>
                ) : null}
                {selectedService.description ? <p className="mt-1 text-slate-600">{selectedService.description}</p> : null}
                {selectedService.classMeta?.isClass && selectedService.classMeta.classParticipantInstructions ? (
                  <p className="mt-2 whitespace-pre-wrap rounded-md bg-white px-3 py-2 text-xs text-slate-600">{selectedService.classMeta.classParticipantInstructions}</p>
                ) : null}
              </div>
            ) : null}

            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                <CalendarDays size={16} />
                Available times
              </div>
              {slotLoading ? (
                <p className="text-sm text-slate-500">Loading times...</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {selectedService?.classMeta?.isClass
                    ? 'No open class times were found for the selected class and location.'
                    : 'No open times were found for the selected service and location.'}
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {slots.map((slot) => (
                    <button
                      type="button"
                      key={slot.slotKey}
                      onClick={() => setSelectedSlot(slot)}
                      className={`rounded-md border px-3 py-2 text-left text-sm ${selectedSlot?.slotKey === slot.slotKey ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                    >
                      <span className="block font-medium">{formatSlot(slot.startTime)}</span>
                      <span className="text-xs opacity-80">Care team</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-3 rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <MapPin size={16} />
              Your details
            </div>
            <input className="hidden" value={form.website} onChange={(event) => setForm({ ...form, website: event.target.value })} tabIndex={-1} autoComplete="off" />
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="First name" value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} />
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Last name" value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} />
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" type="date" value={form.dob} onChange={(event) => setForm({ ...form, dob: event.target.value })} />
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" type="email" placeholder="Email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
            <textarea className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={form.communicationTermsAccepted} onChange={(event) => setForm({ ...form, communicationTermsAccepted: event.target.checked })} />
              <span>I agree to be contacted about this booking request.</span>
            </label>
            <button
              type="button"
              disabled={submitting || !selectedSlot || !form.communicationTermsAccepted}
              onClick={() => void submit()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle2 size={16} />
              {submitting
                ? 'Submitting...'
                : selectedService?.classMeta?.isClass
                  ? (link.mode === 'CONFIRM' ? 'Book class' : 'Request class booking')
                  : link.mode === 'CONFIRM' ? 'Confirm booking' : 'Request booking'}
            </button>
          </aside>
        </section>
      </main>
    </div>
  );
}
